import { FC, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import {
  StyledAlsoLikeDesktop,
  StyledAlsoLikeMobile,
  StyledButtonLeft,
  StyledButtonRight,
  StyledProducts,
  StyledSlideItem,
} from './styles';
import { Typography, Button, Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import CardControlled from '../../../components/CardOurCollection/CardControlled';
import { reducerTypes, sagaTypes } from '../../../store/Product/types';
import { IProduct } from '../../../interfaces/product';
import { NextArrowIcon, PrevArrowIcon } from '../../../icons';
import { CardGridContainer } from '../../../components/CardOurCollection/CardGridContainer';
import styles from './styles.module.css';
import LocalizedLink from '../../../components/Link/localized-link';

export type tGridTemplateColumns = '1fr 1fr' | '1fr';

interface IAlsoLikesProps {
  ssrProduct?: IProduct;
  ssrAlsoLikedProducts?: IProduct[];
}

const AlsoLikes: FC<IAlsoLikesProps> = ({ ssrProduct, ssrAlsoLikedProducts }) => {
  const dispatch = useAppDispatch();
  const producsState = useAppSelector((st) => st?.products);

  const productRef = useRef<IProduct | null>(ssrProduct ?? null);
  const locale = useAppSelector((st) => st.locale);
  const { selectedCurrency } = useAppSelector((st) => st.currency);
  const [gridTemplateColumns, setGridTemplateColumns] = useState<tGridTemplateColumns>('1fr 1fr');
  const [timeClicked, setTimeClicked] = useState<number>(0);
  const [, setOpen] = useState<boolean>(false);

  const [product, setProduct] = useState<IProduct | undefined>(ssrProduct);
  const [alsoLikedProducts, setAlsoLikedProducts] = useState<IProduct[]>(ssrAlsoLikedProducts ?? []);

  useEffect(() => {
    if (producsState.product) {
      setProduct(producsState.product);
      productRef.current = producsState.product;
    }
    if (producsState.alsoLikedProducts && producsState.alsoLikedProducts.length > 0)
      setAlsoLikedProducts(producsState.alsoLikedProducts);
  }, [producsState.alsoLikedProducts, producsState.product]);

  const SlickButtonFix = (props: { children: JSX.Element; slideCount?: number; currentSlide?: number }) => {
    const { children, currentSlide, slideCount, ...others } = props;
    return <span {...others}>{children}</span>;
  };
  const settings = {
    infinite: false,
    variableWidth: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    nextArrow: (
      <SlickButtonFix>
        <StyledButtonRight>
          <NextArrowIcon size={9} />
        </StyledButtonRight>
      </SlickButtonFix>
    ),
    prevArrow: (
      <SlickButtonFix>
        <StyledButtonLeft>
          <PrevArrowIcon size={9} />
        </StyledButtonLeft>
      </SlickButtonFix>
    ),
  };

  const getAlsoLikedProducts = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const curr = params.get('curr');
    dispatch({ type: reducerTypes.GET_ALSO_LIKED_PRODUCTS, payload: [] });
    dispatch({
      type: sagaTypes.GET_ALSO_LIKED_PRODUCTS,
      payload: `${product?.id}?curr=${curr || selectedCurrency}`,
    });
  }, [dispatch, product?.id, selectedCurrency]);

  useEffect(() => {
    if (product && product?.id !== productRef.current?.id) {
      getAlsoLikedProducts();
      productRef.current = product;
    }
  }, [getAlsoLikedProducts, product]);

  if (!alsoLikedProducts.length) return null;

  return (
    <>
      <StyledAlsoLikeMobile>
        <CardGridContainer
          gridColumns={gridTemplateColumns}
          products={alsoLikedProducts.slice(0, 8)}
          setGridColumns={setGridTemplateColumns}
          title={locale['product_you_may_also_like']}
          forProductPage={true}
        />
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', margin: '24px 0' }}>
          <Box style={{ alignSelf: 'center' }}>
            <Button
              LinkComponent={LocalizedLink}
              className='link-dark-fix'
              style={{ width: '236px', height: '48px' }}
              variant='outlined'
              onClick={() => {}}
            >
              {' '}
              <Typography variant={'body2'} sx={{ letterSpacing: '2px', fontSize: '14px' }}>
                {locale['product_you_may_also_like_button']}
              </Typography>
            </Button>
          </Box>
        </div>
      </StyledAlsoLikeMobile>
      <StyledAlsoLikeDesktop>
        <Typography
          variant={'h1'}
          component={'p'}
          sx={{
            margin: 'auto',
            textAlign: 'center',
          }}
        >
          {locale['product_you_may_also_like']}
        </Typography>
        <StyledProducts classContainer={styles.container}>
          <StyledSlideItem {...settings}>
            {alsoLikedProducts?.map((item, index) => {
              return (
                <div
                  key={index}
                  style={{ padding: '0 12px' }}
                  onMouseDown={(e) => {
                    setTimeClicked(e.timeStamp);
                  }}
                  onMouseUp={(e) => {
                    if (e.timeStamp - timeClicked < 200 && window.innerWidth > 1200) {
                      setOpen(true);
                    }
                  }}
                >
                  <CardControlled key={index} product={item} columns={gridTemplateColumns} forProductPage={true} />
                </div>
              );
            })}
          </StyledSlideItem>
          <Box style={{ alignSelf: 'center' }}>
            <Button
              LinkComponent={LocalizedLink}
              className='link-dark-fix'
              href={'/all-watches'}
              style={{ width: '256px', height: '48px' }}
              variant='outlined'
              onClick={() => {}}
            >
              {' '}
              <Typography variant={'body2'} sx={{ letterSpacing: '2px', fontSize: '14px' }}>
                {locale['product_you_may_also_like_button']}
              </Typography>
            </Button>
          </Box>
        </StyledProducts>
      </StyledAlsoLikeDesktop>
    </>
  );
};

export default AlsoLikes;
